import { getApiBaseUrl } from './api';

const getClientErrorUrl = () => `${getApiBaseUrl().replace(/\/$/, '')}/client-errors/`;
const DEDUPE_WINDOW_MS = 60_000;
const recentErrors = new Map();
let installed = false;

const safePageUrl = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}`;
};

const errorDetails = (value) => {
  if (value instanceof Error) {
    return { message: value.message || value.name, stack: value.stack || '' };
  }
  if (typeof value === 'string') return { message: value, stack: '' };
  try {
    return { message: JSON.stringify(value), stack: '' };
  } catch (_) {
    return { message: String(value), stack: '' };
  }
};

/** Report only serious, unexpected failures; routine API errors stay local. */
export const reportClientError = (value, context = {}) => {
  if (typeof fetch !== 'function') return Promise.resolve(false);

  const details = errorDetails(value);
  const source = context.source || 'browser';
  const fingerprint = `${source}:${details.message}:${details.stack.slice(0, 300)}`;
  const now = Date.now();
  if ((recentErrors.get(fingerprint) || 0) > now - DEDUPE_WINDOW_MS) {
    return Promise.resolve(false);
  }
  recentErrors.set(fingerprint, now);
  if (recentErrors.size > 100) {
    for (const [key, seenAt] of recentErrors) {
      if (seenAt <= now - DEDUPE_WINDOW_MS) recentErrors.delete(key);
    }
  }

  return fetch(getClientErrorUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    credentials: 'omit',
    body: JSON.stringify({
      source,
      level: context.level || 'error',
      message: details.message.slice(0, 2000),
      stack: `${details.stack}${context.componentStack ? `\n${context.componentStack}` : ''}`.slice(0, 8000),
      url: safePageUrl(),
      app_version: process.env.REACT_APP_VERSION || 'web',
      user_agent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    }),
  })
    .then((response) => response.ok)
    // Observability must never create another user-visible failure or loop.
    .catch(() => false);
};

export const installClientErrorReporting = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportClientError(event.error || event.message, { source: 'browser' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason || 'Unhandled promise rejection', { source: 'browser' });
  });
};
